// Publieke retourstatuspagina (/RetourStatus, opdracht 29-07).
//
// Twee ingangen: de tokenlink uit de retourmail (?token=…) toont de
// status direct; zonder (geldig) token staat er een identificatiekaart
// met retournummer + e-mailadres. Opzet en toon volgen het door JP
// aangeleverde referentie-ontwerp (retourstatus-mockup v4), de kleuren
// zijn het echte shoppalet: oranje brand-orange-600, koppen gray-900
// (#111827), tekst #333333, links blue-600 (#2563EB), vlak gray-50
// (#F9FAFB), lijn gray-200 (#E5E7EB), groen #2E9E5B.
//
// De tijdlijn toont alleen stappen waarvoor de administratie echt een
// bron heeft (de onderweg-stap komt alleen mee als er een
// trackingcode is) — de mapping zelf staat server-side in
// retour_status_pagina.py.
//
// Sessie 134: de route /RetourStatus verwijst door naar /volgen
// (VolgenPagina.tsx), die de kop, tijdlijn en artikelen hieronder
// hergebruikt. Deze pagina zelf blijft ongewijzigd bestaan.

import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Check, Loader2, MessageCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  retourStatusApi,
  vertaalRetourFout,
  type RetourStatusKeuze,
  type RetourStatusPaginaAntwoord,
  type RetourStatusTijdlijnStap,
} from "@/lib/api";
import { formatteerBedrag } from "./retourLogica";
import {
  openChatWidget,
  RetourFooter,
  RetourHeader,
  RETOUR_KAART,
  RETOUR_KOLOM,
  UspBalk,
} from "./RetourShopElementen";

const WHATSAPP_URL = "https://wa.me/31629806571";

// Shopgroen voor afgeronde stappen (JP-palet); geen Tailwind-alias in
// de config, dus als arbitrary value.
const GROEN = "#2E9E5B";

type Weergave = "laden" | "zoeken" | "keuze" | "status";

function formatteerDatum(iso: string | null, metTijd = false): string {
  if (!iso) return "";
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return "";
  const opties: Intl.DateTimeFormatOptions = metTijd
    ? {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }
    : { weekday: "long", day: "numeric", month: "long" };
  return new Intl.DateTimeFormat("nl-NL", opties).format(datum);
}

// Nette weergavenaam van de vervoerder op het retourlabel
// (tracking-status 29-07); onbekende waarden met beginkapitaal.
const VERVOERDER_NAMEN: Record<string, string> = {
  postnl: "PostNL",
  dhl: "DHL",
  dpd: "DPD",
  gls: "GLS",
  ups: "UPS",
};

function vervoerderNaam(ruw: string): string {
  const sleutel = ruw.trim().toLowerCase();
  return (
    VERVOERDER_NAMEN[sleutel] ??
    ruw.trim().charAt(0).toUpperCase() + ruw.trim().slice(1)
  );
}

function formatteerDatumKort(iso: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return "";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "long",
  }).format(datum);
}

// Chipkleur en -tekst per status; de chip vat de stand in klanttaal
// samen (zelfde blauwtint als de links, groen zodra alles klaar is).
function chipVoor(data: RetourStatusPaginaAntwoord): {
  tekst_sleutel: string;
  klassen: string;
} {
  if (data.fase === "gesloten") {
    return {
      tekst_sleutel: "chip_gesloten",
      klassen: "bg-gray-100 text-gray-600",
    };
  }
  if (data.status === "afgehandeld") {
    return {
      tekst_sleutel: "chip_afgehandeld",
      klassen: "bg-[#eaf6ef] text-[#2E9E5B]",
    };
  }
  const per_status: Record<string, string> = {
    aangemeld: "chip_aangemeld",
    beoordeling: "chip_beoordeling",
    label_aangemaakt: "chip_label_aangemaakt",
    onderweg: "chip_onderweg",
    ontvangen: "chip_ontvangen",
  };
  return {
    tekst_sleutel: per_status[data.status] ?? "chip_aangemeld",
    klassen: "bg-blue-50 text-blue-600",
  };
}

function StapBol({ staat }: { staat: RetourStatusTijdlijnStap["staat"] }) {
  if (staat === "klaar") {
    return (
      <span
        aria-hidden
        className="absolute left-0 top-0 grid h-[30px] w-[30px] place-items-center rounded-full border-2"
        style={{ backgroundColor: GROEN, borderColor: GROEN }}
      >
        <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
      </span>
    );
  }
  if (staat === "actief") {
    return (
      <span
        aria-hidden
        className="absolute left-0 top-0 grid h-[30px] w-[30px] place-items-center rounded-full border-2 border-blue-600 bg-white"
      >
        <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="absolute left-0 top-0 h-[30px] w-[30px] rounded-full border-2 border-gray-200 bg-gray-50"
    />
  );
}

export function Tijdlijn({ data }: { data: RetourStatusPaginaAntwoord }) {
  const { t } = useTranslation();
  const beoordeling = data.fase === "beoordeling";

  function stapTeksten(stap: RetourStatusTijdlijnStap): {
    titel: string;
    tekst: string | null;
  } {
    switch (stap.stap) {
      case "aangemeld":
        return {
          titel: t("retour.statuspagina.stap_aangemeld_titel"),
          tekst: beoordeling
            ? t("retour.statuspagina.stap_aangemeld_beoordeling_tekst")
            : t("retour.statuspagina.stap_aangemeld_tekst"),
        };
      case "onderweg":
        return {
          titel: t("retour.statuspagina.stap_onderweg_titel"),
          // Met bekende vervoerder wordt de tekst concreet: "Afgegeven
          // bij PostNL, ..." (tracking-status 29-07); de datum/tijd van
          // de eerste scan staat als stap-datum erboven.
          tekst:
            stap.staat === "wachtend"
              ? t("retour.statuspagina.stap_onderweg_wacht_tekst")
              : stap.vervoerder
                ? t("retour.statuspagina.stap_onderweg_afgegeven_tekst", {
                    vervoerder: vervoerderNaam(stap.vervoerder),
                  })
                : t("retour.statuspagina.stap_onderweg_tekst"),
        };
      case "ontvangen":
        return {
          titel: t("retour.statuspagina.stap_ontvangen_titel"),
          tekst:
            stap.staat === "actief"
              ? t("retour.statuspagina.stap_ontvangen_tekst")
              : null,
        };
      case "afgehandeld":
      default: {
        const per_afhandeling: Record<string, string> = {
          terugbetaling: "retour.statuspagina.stap_afgehandeld_terugbetaling",
          kortingscode: "retour.statuspagina.stap_afgehandeld_kortingscode",
          vervanging: "retour.statuspagina.stap_afgehandeld_vervanging",
        };
        const sleutel = data.afhandeling
          ? per_afhandeling[data.afhandeling]
          : null;
        return {
          titel: t("retour.statuspagina.stap_afgehandeld_titel"),
          tekst: sleutel ? t(sleutel) : null,
        };
      }
    }
  }

  return (
    // Tekstmaten en afstanden van de kaarten op /retour (sessie 137):
    // stapkop 15 px vet, tekst 14 px, kaders en knop als op /retour.
    <ol className="mt-5 list-none">
      {data.tijdlijn.map((stap, index) => {
        const laatste = index === data.tijdlijn.length - 1;
        const { titel, tekst } = stapTeksten(stap);
        const datum = formatteerDatum(stap.datum, true);
        return (
          <li
            key={stap.stap}
            className={`relative pl-[46px] ${laatste ? "" : "pb-6"}`}
          >
            {!laatste && (
              <span
                aria-hidden
                className="absolute bottom-0 left-[14px] top-[30px] w-0.5"
                style={{
                  backgroundColor: stap.staat === "klaar" ? GROEN : "#E5E7EB",
                }}
              />
            )}
            <StapBol staat={stap.staat} />
            <h3
              className={`text-[15px] font-bold ${
                stap.staat === "wachtend" ? "text-gray-500" : "text-gray-900"
              }`}
            >
              {titel}
            </h3>
            {datum && <p className="text-[13.5px] text-gray-500">{datum}</p>}
            {tekst && (
              <p className="mt-1 max-w-[520px] text-[14px] text-[#333333]">
                {tekst}
              </p>
            )}
            {stap.stap === "onderweg" && stap.track_trace_url && (
              <a
                href={stap.track_trace_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center rounded-lg border-[1.5px] border-gray-900 bg-white px-[18px] py-2.5 text-[15px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {t("retour.statuspagina.stap_onderweg_volgen")}
              </a>
            )}
            {stap.stap === "ontvangen" &&
              stap.staat === "actief" &&
              data.verwerk_uiterlijk && (
                <div className="mt-3 max-w-[520px] rounded-[10px] border border-blue-100 bg-blue-50 px-4 py-3 text-[13.5px] text-[#333333]">
                  <Trans
                    i18nKey="retour.statuspagina.belofte"
                    values={{
                      dagen: data.verwerking_werkdagen,
                      datum: formatteerDatum(data.verwerk_uiterlijk),
                    }}
                    components={[
                      <span key="0" />,
                      <strong key="1" className="text-gray-900" />,
                    ]}
                  />
                </div>
              )}
          </li>
        );
      })}
    </ol>
  );
}

// Kaart en kop als op /retour, regels en totalen als het overzicht in
// stap 4 daar (sessie 137). `kaal`: zonder eigen kaart, voor binnen de
// uitgeklapte retourkaart op /volgen (geen kaart in een kaart).
export function ArtikelenKaart({
  data,
  kaal = false,
}: {
  data: RetourStatusPaginaAntwoord;
  kaal?: boolean;
}) {
  const { t } = useTranslation();
  const kortingscode = data.afhandeling === "kortingscode";
  return (
    <section
      className={
        kaal ? "mt-5 border-t border-gray-200 pt-5" : `mt-4 ${RETOUR_KAART}`
      }
    >
      <h2 className="text-[16px] font-bold text-gray-900">
        {t("retour.statuspagina.artikelen_titel")}
      </h2>
      <div className="mt-1">
        {data.artikelen.map((artikel, index) => (
          <div
            key={`${artikel.naam}-${index}`}
            className={`flex justify-between gap-3.5 py-3.5 text-[15px] ${
              index === data.artikelen.length - 1
                ? ""
                : "border-b border-gray-200"
            }`}
          >
            <div className="min-w-0">
              <div className="font-bold text-gray-900">
                {artikel.naam}
                {artikel.aantal > 1 && (
                  <span className="ml-1.5 font-normal text-gray-500">
                    {t("retour.statuspagina.artikel_aantal", {
                      aantal: artikel.aantal,
                    })}
                  </span>
                )}
              </div>
              <div className="text-[13.5px] text-gray-500">
                {t("retour.statuspagina.artikel_reden", {
                  reden: t(`retour.redenen.${artikel.reden}`, {
                    defaultValue: artikel.reden,
                  }),
                })}
                {!artikel.verrekend && (
                  <>
                    {" · "}
                    {t("retour.statuspagina.artikel_uitgesloten")}
                  </>
                )}
              </div>
            </div>
            <div className="whitespace-nowrap font-bold text-[#333333]">
              {formatteerBedrag(artikel.prijs_cents * artikel.aantal)}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between border-t border-gray-200 pt-2.5 text-[15px] text-[#333333]">
        <span>{t("retour.statuspagina.artikelbedrag")}</span>
        <span>{formatteerBedrag(data.artikelbedrag_cents)}</span>
      </div>
      {data.verzendkosten_cents > 0 && (
        <div className="flex justify-between pt-2.5 text-[15px] text-[#333333]">
          <span>{t("retour.statuspagina.verzendkosten")}</span>
          <span>{formatteerBedrag(data.verzendkosten_cents)}</span>
        </div>
      )}
      {data.verrekening_cents > 0 && (
        <div className="flex justify-between pt-2.5 text-[15px] text-gray-500">
          <span>{t("retour.statuspagina.retourkosten_verrekend")}</span>
          <span>− {formatteerBedrag(data.verrekening_cents)}</span>
        </div>
      )}
      <div className="mt-2.5 flex justify-between border-t-2 border-gray-900 pt-3 text-[16px] font-bold text-gray-900">
        <span>
          {kortingscode
            ? t("retour.statuspagina.terug_totaal_kortingscode")
            : t("retour.statuspagina.terug_totaal")}
        </span>
        <span>{formatteerBedrag(data.terug_cents)}</span>
      </div>
      {data.gedeeltelijk && (
        <p className="mt-2.5 text-[13.5px] text-gray-500">
          {t("retour.statuspagina.gedeeltelijk_uitleg")}
        </p>
      )}
    </section>
  );
}

// Status in woorden (de chiptekst); los geëxporteerd voor de
// ingeklapte retourkaart op de volgpagina (sessie 134).
export function RetourStatusWoord({
  data,
}: {
  data: RetourStatusPaginaAntwoord;
}) {
  const { t } = useTranslation();
  return <>{t(`retour.statuspagina.${chipVoor(data).tekst_sleutel}`)}</>;
}

// Gesloten-fase: uitleg plus eventuele reden (gedeeld met de volgpagina).
export function RetourGeslotenBlok({
  data,
}: {
  data: RetourStatusPaginaAntwoord;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-5 max-w-[520px]">
      <p className="text-[14px] text-[#333333]">
        {t("retour.statuspagina.gesloten_tekst")}
      </p>
      {data.annulering_reden && (
        <div className="mt-3 rounded-md border border-border bg-white p-4 text-[13.5px]">
          <p className="font-semibold text-gray-900">
            {t("retour.statuspagina.gesloten_reden_titel")}
          </p>
          <p className="mt-1 text-[#333333]">{data.annulering_reden}</p>
        </div>
      )}
    </div>
  );
}

// De statuskaart (kop, meta, chip, tijdlijn of gesloten-uitleg);
// uitgelicht zodat de volgpagina (sessie 134) dezelfde weergave toont.
export function RetourStatusKop({
  data,
}: {
  data: RetourStatusPaginaAntwoord;
}) {
  const { t } = useTranslation();
  const chip = chipVoor(data);
  return (
    <div className={RETOUR_KAART}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {/* Kop en kaart in de maat van /retour (sessie 136). */}
          <h1 className="text-2xl font-bold tracking-tight text-brand-navy">
            {data.fase === "gesloten"
              ? t("retour.statuspagina.kop_gesloten")
              : data.voornaam
                ? t("retour.statuspagina.kop_hoi", {
                    voornaam: data.voornaam,
                  })
                : t("retour.statuspagina.kop_zonder_naam")}
          </h1>
          <p className="mt-1 text-[13.5px] text-gray-500">
            {data.ordernummer
              ? t("retour.statuspagina.meta", {
                  retournummer: data.retournummer,
                  ordernummer: data.ordernummer,
                  datum: formatteerDatumKort(data.aangemeld_op),
                })
              : t("retour.statuspagina.meta_zonder_order", {
                  retournummer: data.retournummer,
                  datum: formatteerDatumKort(data.aangemeld_op),
                })}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13.5px] font-bold ${chip.klassen}`}
        >
          <span aria-hidden className="h-2 w-2 rounded-full bg-current" />
          {t(`retour.statuspagina.${chip.tekst_sleutel}`)}
        </span>
      </div>

      {data.fase === "gesloten" ? (
        <RetourGeslotenBlok data={data} />
      ) : (
        <Tijdlijn data={data} />
      )}
    </div>
  );
}

function HulpKaart() {
  const { t } = useTranslation();
  return (
    <section className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 p-6 md:p-7">
      <p className="max-w-[380px] text-[14.5px] text-gray-500">
        {t("retour.statuspagina.hulp_tekst")}
      </p>
      <div className="flex flex-wrap gap-2.5">
        <button
          type="button"
          onClick={openChatWidget}
          className="inline-flex items-center gap-2 rounded-lg border-[1.5px] border-gray-900 bg-white px-[18px] py-2.5 text-[15px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t("retour.statuspagina.hulp_chat")}
        </button>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-lg border-[1.5px] border-gray-900 bg-white px-[18px] py-2.5 text-[15px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t("retour.statuspagina.hulp_whatsapp")}
        </a>
      </div>
    </section>
  );
}

export function RetourStatusPagina() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [weergave, setWeergave] = useState<Weergave>(
    token ? "laden" : "zoeken",
  );
  const [data, setData] = useState<RetourStatusPaginaAntwoord | null>(null);
  const [keuzes, setKeuzes] = useState<RetourStatusKeuze[]>([]);
  const [fout, setFout] = useState<string | null>(null);
  const [nummer, setNummer] = useState("");
  const [email, setEmail] = useState("");
  const [bezig, setBezig] = useState(false);

  // Ingang 1: tokenlink uit de mail. Ongeldig of verlopen token →
  // terugvallen op de identificatiekaart met een nette melding.
  useEffect(() => {
    if (!token) return;
    let actueel = true;
    (async () => {
      try {
        const antwoord = await retourStatusApi.viaToken(token);
        if (!actueel) return;
        setData(antwoord);
        setWeergave("status");
      } catch {
        if (!actueel) return;
        setFout(t("retour.statuspagina.link_ongeldig"));
        setWeergave("zoeken");
      }
    })();
    return () => {
      actueel = false;
    };
  }, [token, t]);

  // Ingang 2: retour- of ordernummer + e-mailadres. Een ordernummer
  // met meerdere retouren levert een keuzelijstje; de klant kiest en
  // dezelfde route haalt dan de status op retournummer op.
  const zoekNummer = useCallback(
    async (waarde: string) => {
      setBezig(true);
      setFout(null);
      try {
        const antwoord = await retourStatusApi.zoeken(
          waarde.trim(),
          email.trim(),
        );
        if (antwoord.status) {
          setData(antwoord.status);
          setKeuzes([]);
          setWeergave("status");
        } else {
          setKeuzes(antwoord.keuzes);
          setWeergave("keuze");
        }
        window.scrollTo({ top: 0 });
      } catch (err) {
        setFout(vertaalRetourFout(err, t("retour.fout_server")));
      } finally {
        setBezig(false);
      }
    },
    [email, t],
  );

  const zoek = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void zoekNummer(nummer);
    },
    [zoekNummer, nummer],
  );

  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      <RetourHeader />
      <UspBalk />

      <main className={`${RETOUR_KOLOM} flex-1 py-8`}>
        <div className="mx-auto w-full max-w-[720px]">
          {weergave === "laden" && (
            <div
              className="flex items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 p-10 text-[15px] text-gray-500"
              role="status"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              {t("retour.statuspagina.laden")}
            </div>
          )}

          {weergave === "zoeken" && (
            <section aria-label={t("retour.statuspagina.titel")}>
              <form
                onSubmit={zoek}
                noValidate
                className="rounded-xl border border-gray-200 bg-gray-50 p-6 md:p-7"
              >
                <h1 className="text-[26px] font-bold tracking-tight text-gray-900 md:text-[28px]">
                  {t("retour.statuspagina.titel")}
                </h1>
                <p className="mt-2 max-w-[520px] text-[15px] text-[#333333]">
                  {t("retour.statuspagina.intro")}
                </p>
                {fout && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="mt-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
                  >
                    {fout}
                  </div>
                )}
                <div className="mt-4 space-y-1.5">
                  <Label htmlFor="retourstatus-nummer">
                    {t("retour.statuspagina.retournummer_label")}
                  </Label>
                  <Input
                    id="retourstatus-nummer"
                    name="nummer"
                    placeholder={t(
                      "retour.statuspagina.retournummer_placeholder",
                    )}
                    autoComplete="off"
                    required
                    value={nummer}
                    onChange={(e) => setNummer(e.target.value)}
                    className="h-11 bg-white"
                  />
                </div>
                <div className="mt-4 space-y-1.5">
                  <Label htmlFor="retourstatus-email">
                    {t("retour.statuspagina.email_label")}
                  </Label>
                  <Input
                    id="retourstatus-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 bg-white"
                  />
                  <p className="text-[12.5px] text-gray-500">
                    {t("retour.statuspagina.hint")}
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={bezig}
                  className="mt-5 h-11 w-full text-[15px] sm:w-auto sm:px-6"
                >
                  {bezig && (
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden
                    />
                  )}
                  {bezig
                    ? t("retour.statuspagina.bezig")
                    : t("retour.statuspagina.bekijken")}
                </Button>
              </form>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 p-6 md:p-7">
                <p className="max-w-[380px] text-[14.5px] text-gray-500">
                  {t("retour.statuspagina.nummer_kwijt")}
                </p>
                <button
                  type="button"
                  onClick={openChatWidget}
                  className="inline-flex items-center gap-2 rounded-lg border-[1.5px] border-gray-900 bg-white px-[18px] py-2.5 text-[15px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden />
                  {t("retour.statuspagina.hulp_chat")}
                </button>
              </div>
            </section>
          )}

          {weergave === "keuze" && keuzes.length > 0 && (
            <section
              aria-label={t("retour.statuspagina.keuze_titel")}
              className="rounded-xl border border-gray-200 bg-gray-50 p-6 md:p-7"
            >
              <h1 className="text-[24px] font-bold tracking-tight text-gray-900 md:text-[26px]">
                {t("retour.statuspagina.keuze_titel")}
              </h1>
              <p className="mt-2 max-w-[520px] text-[15px] text-[#333333]">
                {t("retour.statuspagina.keuze_intro")}
              </p>
              <ul className="mt-4 list-none">
                {keuzes.map((keuze) => (
                  <li
                    key={keuze.retournummer}
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 py-3.5 first:border-t-0"
                  >
                    <div className="min-w-0">
                      <div className="text-[15px] font-bold text-gray-900">
                        {keuze.retournummer}
                        <span className="ml-2 font-normal text-gray-500">
                          {t("retour.statuspagina.keuze_aangemeld", {
                            datum: formatteerDatumKort(keuze.aangemeld_op),
                          })}
                        </span>
                      </div>
                      {keuze.artikelen && (
                        <div className="text-[13.5px] text-gray-500">
                          {keuze.artikelen}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={bezig}
                      onClick={() => void zoekNummer(keuze.retournummer)}
                      className="inline-flex items-center rounded-lg border-[1.5px] border-gray-900 bg-white px-4 py-2 text-[14px] font-bold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      {t("retour.statuspagina.keuze_bekijk")}
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  setKeuzes([]);
                  setFout(null);
                  setWeergave("zoeken");
                }}
                className="mt-4 inline-block text-[14px] text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("retour.statuspagina.andere_retour")}
              </button>
            </section>
          )}

          {weergave === "status" && data && (
            <section aria-label={t("retour.resultaat.status_titel")}>
              <RetourStatusKop data={data} />

              {data.fase !== "gesloten" && data.artikelen.length > 0 && (
                <ArtikelenKaart data={data} />
              )}
              <HulpKaart />

              <button
                type="button"
                onClick={() => {
                  setData(null);
                  setKeuzes([]);
                  setFout(null);
                  setWeergave("zoeken");
                  window.scrollTo({ top: 0 });
                }}
                className="mt-4 inline-block text-[14px] text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("retour.statuspagina.andere_retour")}
              </button>
            </section>
          )}
        </div>
      </main>

      <RetourFooter />
    </div>
  );
}

export default RetourStatusPagina;
